import type {KeyTextField} from '@prismicio/client'
import {PrismicNextImage} from '@prismicio/next/pages'
import {PrismicRichText} from '@prismicio/react'
import cn from 'classnames'
import {AnimatePresence, motion} from 'framer-motion'
import type {HeroV3SliceCareersVariation, HeroV3SliceCareersVariationPrimaryPortraitsItem} from 'prismicio-types'
import {useMemo, useState} from 'react'
import AnimatedCounter from 'src/common/AnimatedCounter'
import {ButtonV2} from 'src/common/ButtonV2'
import {Container} from 'src/common/Container'
import type {Context} from 'src/common/types'

/**
 * Splits a string into three chunks:
 * 1. Everything before the first digit
 * 2. All consecutive digits
 * 3. Everything after the digits
 */
const splitStringWithDigits = (str: string): [string, number, string] => {
  if (!str) {
    return ['', 0, '']
  }

  const match = str.match(/^([^0-9]*)([0-9]+)(.*)$/)

  if (!match) {
    // If no digits found, return the whole string as the first chunk
    return [str, 0, '']
  }

  const [, beforeDigits, digits, afterDigits] = match
  return [beforeDigits, parseInt(digits, 10), afterDigits]
}

type CareersVariationProps = {
  slice: HeroV3SliceCareersVariation & {
    slice_type: string
    variation: string
  }
  componentIndex: number
  context: Context
}

export const CareersVariation = ({slice: {primary, ...slice}, componentIndex, context}: CareersVariationProps) => {
  const {portraits, primary_button_href, primary_button_text} = primary

  return (
    <Container
      backgroundColor={primary.background_color}
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      className="px-6 !pb-10 pt-[100px] md:px-10 md:!pb-20 md:pt-[180px] lg:px-5 xl:px-0"
    >
      <div className="flex max-w-[1044px] flex-col items-center gap-3 md:gap-6" data-aos="fade-up" data-aos-offset="0">
        {portraits.length > 0 && <FacePile portraits={portraits} employeeCount={primary.employee_count} />}
        <div>
          <PrismicRichText
            field={primary.heading}
            components={{
              heading2: ({children}) => (
                <h1 className="lg:text-tg-h1 text-center text-40 font-normal leading-[42px] tracking-[-.4px] text-moss-black">
                  {children}
                </h1>
              ),
            }}
          />
        </div>
        <PrismicRichText
          field={primary.description}
          components={{
            paragraph: ({children}) => (
              <p className="text-tg-body-l max-w-[432px] text-center text-moss-black/[60%]">{children}</p>
            ),
          }}
        />

        {primary_button_text && (
          <ButtonV2
            variant="primary"
            trackingData={{
              mktgId: 'primary-cta',
              location: `hero-v3-careers-variation-${componentIndex}`,
              pageName: context.pageUid,
              dataType: 'button',
            }}
            href={primary_button_href || '#'}
          >
            {primary_button_text}
          </ButtonV2>
        )}
      </div>
    </Container>
  )
}

const FacePile = ({
  portraits,
  employeeCount,
}: {
  portraits: HeroV3SliceCareersVariationPrimaryPortraitsItem[]
  employeeCount: KeyTextField
}) => {
  const [isHovered, setIsHovered] = useState(false)

  const selectedPortraits = useMemo(() => {
    if (!portraits.length) {
      return []
    }

    if (portraits.length > 3) {
      return portraits
        .slice()
        .sort(() => Math.random() - 0.5)
        .slice(0, 3)
    }

    return portraits
  }, [portraits])

  // Convert employeeCount to number for the AnimatedCounter
  const [firstChunk, baseCount, thirdChunk] = splitStringWithDigits(employeeCount || '0')
  const displayCount = isHovered ? baseCount + 1 : baseCount

  return (
    <div
      className="group/pile isolate flex items-center"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {selectedPortraits.map((portrait, index) => (
        <div
          key={index}
          className={cn(
            'flex h-[34px] w-[34px] items-center justify-center overflow-hidden rounded-full border-[2px] border-solid border-moss-grey-100 bg-moss-grey-200 transition-all duration-300 ease-in-out',
            {
              'ml-[-5px]': !isHovered,
              'ml-0': isHovered,
            },
          )}
          style={{zIndex: 0 - index}}
        >
          <PrismicNextImage key={index} field={portrait.image} alt="" />
        </div>
      ))}
      <div
        className={cn(
          '-z-10 flex h-[34px] w-[34px] items-center justify-center rounded-full border-[2px] border-solid border-moss-grey-100 bg-moss-black transition-all duration-300 ease-in-out',
          {
            'ml-[-5px]': !isHovered,
            'ml-0': isHovered,
          },
        )}
      >
        <div className="flex items-center gap-[.42px] transition-all duration-300 ease-in-out">
          {firstChunk && (
            <span className="text-[7px] font-[600] leading-[normal] tracking-[.42px] text-moss-white">
              {firstChunk}
            </span>
          )}
          <AnimatedCounter
            value={displayCount}
            fontSize={7}
            textColor="white"
            fontWeight={600}
            counterStyle={{
              letterSpacing: '.42px',
              lineHeight: 'normal',
            }}
            gap={0.42}
            horizontalPadding={0}
          />
          <AnimatePresence mode="wait">
            {isHovered && thirdChunk && (
              <motion.span
                key="thirdChunk"
                initial={{opacity: 0, scale: 0.8, width: 0}}
                animate={{opacity: 1, scale: 1, width: 'auto'}}
                exit={{opacity: 0, scale: 0.8, width: 0}}
                transition={{
                  duration: 0.2,
                  ease: 'easeInOut',
                  delay: 0.3,
                }}
                className="overflow-hidden whitespace-nowrap text-[7px] font-[600] leading-[normal] tracking-[.42px] text-moss-white"
              >
                {thirdChunk}
              </motion.span>
            )}
          </AnimatePresence>
        </div>
      </div>
    </div>
  )
}
